<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { reactive, ref, toRaw } from 'vue';
import _compact from 'lodash/compact';
import _uniq from 'lodash/uniq';

import KRadioField from '@/components/fields/KRadioField.vue';
import KTextareaField from '@/components/fields/KTextareaField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

type JobIdsDataType = 'jobIds';
type SerialNumberDataType = 'serialNumber';
type CombinedDataDataType = 'combinedData';
type CombinedAddressDataType = 'combinedAddress';

export interface InitialData {
  dataType?:
    | JobIdsDataType
    | SerialNumberDataType
    | CombinedDataDataType
    | CombinedAddressDataType
    | null;
  data?: string | null;
}

export interface ValidData {
  dataType:
    | JobIdsDataType
    | SerialNumberDataType
    | CombinedDataDataType
    | CombinedAddressDataType;
  data: string;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const jobIdDataType: JobIdsDataType = 'jobIds';
const serialNumberDataType: SerialNumberDataType = 'serialNumber';
const combinedDataDataType: CombinedDataDataType = 'combinedData';
const combinedAddressDataType: CombinedAddressDataType = 'combinedAddress';

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  const _formData = {
    ...toRaw(formData),
    data: parseTextareaData(formData.data as string),
  };
  emit('submit', event, _formData as ValidData);
});

function parseTextareaData(value: string): string {
  const lines = value.trim().split(/\r|\r\n|\n/);
  const linesLength = lines.length;
  let items: string[] = [];
  if (linesLength === 1) {
    items = value.split(',').map((x: string) => x.trim());
  } else {
    items = lines.map((x: string) => x.trim());
  }
  return _uniq(_compact(items))
    .join(',')
    .replace(/,+/g, ',')
    .replace(/^,/, '')
    .replace(/,$/, '');
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.advancedSearchMultiSearchDialogForm"
    data-testid="advancedSearchMultiSearchDialogForm"
    @submit.prevent="handleSubmit"
  >
    <h4 :class="$style.title">
      Choose the type of data to enter in the text field
    </h4>

    <v-radio-group
      v-model="formData.dataType"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.radioGroup"
    >
      <KRadioField label="Job IDs" :value="jobIdDataType" />
      <KRadioField label="Serial number" :value="serialNumberDataType" />
      <KRadioField label="Combined data" :value="combinedDataDataType" />
      <KRadioField label="Combined address" :value="combinedAddressDataType" />
    </v-radio-group>

    <h4 :class="$style.title">
      Insert a column from Excel or a line with commas
    </h4>
    <KTextareaField
      v-model="formData.data"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :rows="4"
      placeholder="E.g 64534, 67854, 67823, 87658, 78654, 89564, 78654..."
    />
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.advancedSearchMultiSearchDialogForm {
}
  .title {
    font-weight: 400;
    margin-top: 0;
    margin-bottom: 1rem;
  }
  .radioGroup {
    margin-bottom: 0.5rem;
  }
</style>
